import "@/App.css";
import { useState, useEffect } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, ArrowRight, Sparkles, Calendar, CheckCircle, Instagram, Facebook } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import IndustryPage from "./IndustryPage";
import AudioDemoPlayer from "./components/AudioDemoPlayer";
import PrivacyPolicy from "./PrivacyPolicy";
import Header from "./components/Header";
import GlobalFooter from "./components/GlobalFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cal.com booking link
const CALENDLY_LINK = "https://cal.com/modern-path-ai-pludn4";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-bg min-h-screen flex items-center justify-center relative px-4 py-20" data-testid="hero-section" aria-labelledby="hero-heading">
      <div className="max-w-[1400px] mx-auto text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="space-y-8"
        >
          {/* Caption */}
          <motion.p 
            variants={fadeUp}
            className="text-sm md:text-base font-mono uppercase tracking-[0.3em] text-[#A0A0A0]"
          >
            Voice AI for Service Businesses
          </motion.p>
          
          {/* Main Headline - Single H1 for SEO */}
          <motion.h1 
            id="hero-heading"
            variants={fadeUp}
            className="hero-headline text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase leading-[0.9]"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            <span className="text-[#F0F2F5]">Stop Missing Calls.</span>
            <br />
            <span className="text-[#00FFFF]">Start Closing Leads with Voice AI.</span>
          </motion.h1>
          
          {/* Subheadline */}
          <motion.p 
            variants={fadeUp}
            className="subheadline text-base md:text-lg lg:text-xl text-[#A0A0A0] max-w-2xl mx-auto font-light leading-relaxed"
          >
            The 24/7 Voice AI Agent for your service business. We answer, we qualify, and we book—while you work.
          </motion.p>
          
          {/* CTA Button */}
          <motion.div variants={fadeUp} className="pt-4">
            <a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-primary inline-flex items-center gap-3 rounded-sm"
              data-testid="hero-cta-btn"
            >
              Set an Appointment
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Gradient orb background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00FFFF] opacity-[0.03] blur-[120px] rounded-full pointer-events-none" />
    </section>
  );
};

// Audio Demo Section - "The Audio Proof Module"
const AudioDemoSection = () => {
  return (
    <section className="py-16 md:py-24 px-4 bg-[#0D0D0D] relative overflow-hidden" data-testid="audio-demo-section">
      {/* Subtle gradient accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#FF6600] opacity-[0.03] blur-[150px] rounded-full pointer-events-none" />
      
      <div className="max-w-[1400px] mx-auto relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center space-y-8"
        >
          {/* Section Header */}
          <motion.p 
            variants={fadeUp}
            className="text-sm font-mono uppercase tracking-[0.3em] text-[#FF6600]"
          >
            Hear It In Action
          </motion.p>
          <motion.h2 
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Don't Take Our Word For It
          </motion.h2>
          <motion.p 
            variants={fadeUp}
            className="text-[#A0A0A0] max-w-xl mx-auto text-base md:text-lg"
          >
            Listen to Amy, our Voice AI, handle a real emergency call from a frustrated homeowner.
          </motion.p>
          
          {/* Audio Player */}
          <motion.div variants={fadeUp} className="pt-4">
            <AudioDemoPlayer />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Call Emily QR Section - Interactive Demo Invitation
const CallEmilySection = () => {
  return (
    <section className="py-16 md:py-24 px-4 relative overflow-hidden" data-testid="call-emily-section">
      {/* Background glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00FFFF] opacity-[0.05] blur-[150px] rounded-full pointer-events-none" />
      
      <div className="max-w-[1000px] mx-auto relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          {/* Main Card */}
          <motion.div 
            variants={fadeUp}
            className="relative bg-gradient-to-br from-[#0D0D0D] to-[#151515] border border-white/10 rounded-3xl p-8 md:p-12 overflow-hidden"
          >
            {/* Animated border glow */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#00FFFF]/20 via-transparent to-[#00FFFF]/20 opacity-50 blur-sm" />
            
            <div className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              {/* QR Code Side */}
              <div className="flex-shrink-0">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  {/* Glowing ring around QR */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#00FFFF] to-[#00FFFF] rounded-2xl opacity-20 blur-xl animate-pulse" />
                  
                  <div className="relative bg-white p-4 rounded-2xl shadow-2xl shadow-[#00FFFF]/20">
                    <img 
                      src="/emily-qr-code.png" 
                      alt="Scan to call Emily - Voice AI Demo"
                      className="w-48 h-48 md:w-56 md:h-56"
                    />
                  </div>
                  
                  {/* Scan prompt */}
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#00FFFF] text-[#0A0A0A] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                    Scan to Call
                  </div>
                </motion.div>
              </div>
              
              {/* Content Side */}
              <div className="flex-1 text-center lg:text-left">
                <p className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4">
                  Try It Yourself
                </p>
                <h2 
                  className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4"
                  style={{ fontFamily: 'Syne, sans-serif' }}
                >
                  Meet <span className="text-[#00FFFF]">Emily</span>
                </h2>
                <p className="text-lg md:text-xl text-[#A0A0A0] mb-6 leading-relaxed">
                  Our Voice AI is waiting to show you exactly how she can handle calls for <strong className="text-white">your</strong> business. Scan the QR code and experience it firsthand.
                </p>
                
                {/* Benefits list */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                    <CheckCircle className="w-4 h-4 text-[#00FFFF]" />
                    <span>Live conversation</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                    <CheckCircle className="w-4 h-4 text-[#00FFFF]" />
                    <span>No commitment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                    <CheckCircle className="w-4 h-4 text-[#00FFFF]" />
                    <span>See AI in action</span>
                  </div>
                </div>
                
                {/* Phone icon indicator */}
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-12 h-12 rounded-full bg-[#00FFFF]/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#00FFFF]" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#A0A0A0] uppercase tracking-wider">Voice Demo</p>
                    <p className="text-white font-semibold">Call Emily Now</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Industry Grid - Hexagonal Diamond Layout
const IndustryGrid = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  
  const categories = [
    { id: 'all', label: 'All' },
    { id: 'home', label: 'Home & Property' },
    { id: 'auto', label: 'Auto & Mechanical' },
    { id: 'professional', label: 'Professional' },
    { id: 'wellness', label: 'Health & Wellness' }
  ];
  
  const industries = [
    // Home & Property
    { title: "Home Services", description: "Plumbing, HVAC & electrical leads 24/7.", image: "/images/industries/home-services.jpg", category: "home", alt: "Voice AI for plumbers and HVAC contractors", slug: "home-services" },
    { title: "Appliance Repair", description: "Capture every broken appliance call.", image: "/images/industries/appliance-repair.jpg", category: "home", alt: "Voice AI for appliance repair services", slug: "appliance-repair" },
    { title: "Garage Door", description: "24/7 emergency door & opener response.", image: "/images/industries/garage-door.jpg", category: "home", alt: "Automation for garage door companies", slug: "garage-door" },
    { title: "Locksmith", description: "Never miss an emergency lockout call.", image: "/images/industries/locksmith.jpg", category: "home", alt: "Voice AI for locksmith businesses", slug: "locksmith" },
    { title: "Pest Control", description: "Book inspections while you're on-site.", image: "/images/industries/pest-control.jpg", category: "home", alt: "Voice AI for pest control companies", slug: "pest-control" },
    { title: "Landscaping", description: "Schedule lawn care appointments easily.", image: "/images/industries/landscaping.jpg", category: "home", alt: "Automation for landscaping and lawn care", slug: "landscaping" },
    // Auto & Mechanical
    { title: "Auto Repair", description: "Book service while your team works.", image: "/images/industries/auto-repair.jpg", category: "auto", alt: "Voice AI for auto mechanics and repair shops", slug: "auto-repair" },
    { title: "Towing Services", description: "Dispatch-ready AI for emergencies.", image: "/images/industries/towing.jpg", category: "auto", alt: "Voice AI for towing and roadside assistance", slug: "towing" },
    { title: "Auto Detailing", description: "Fill your schedule automatically.", image: "/images/industries/auto-detailing.jpg", category: "auto", alt: "Automation for car detailing businesses", slug: "auto-detailing" },
    // Professional Services
    { title: "Law Firms", description: "Qualify leads before consultations.", image: "/images/industries/legal.jpg", category: "professional", alt: "Automation for law firms and legal intake", slug: "legal" },
    { title: "Accounting", description: "Book tax prep & financial reviews.", image: "/images/industries/accounting.jpg", category: "professional", alt: "Voice AI for accounting firms and CPAs", slug: "accounting" },
    { title: "Real Estate", description: "Capture buyer & seller inquiries.", image: "/images/industries/real-estate.jpg", category: "professional", alt: "Automation for real estate professionals", slug: "real-estate" },
    // Health & Wellness
    { title: "Beauty & Wellness", description: "Automated booking for salons & spas.", image: "/images/industries/beauty.jpg", category: "wellness", alt: "Automation for beauty salons and spas", slug: "beauty" },
    { title: "Pet Care", description: "Let AI handle grooming schedules.", image: "/images/industries/pet-care.jpg", category: "wellness", alt: "Voice AI for pet groomers and veterinarians", slug: "pet-care" },
    { title: "Dental Clinics", description: "Fill cancellations & book checkups.", image: "/images/industries/dental.jpg", category: "wellness", alt: "Voice AI for dental offices and clinics", slug: "dental" },
    { title: "Fitness Studios", description: "Class bookings & membership inquiries.", image: "/images/industries/fitness.jpg", category: "wellness", alt: "Automation for gyms and fitness centers", slug: "fitness" }
  ];
  
  const filteredIndustries = activeCategory === 'all' 
    ? industries 
    : industries.filter(ind => ind.category === activeCategory);

  return (
    <section className="py-20 md:py-32 px-4 overflow-x-hidden" data-testid="industry-section">
      <div className="max-w-[1400px] mx-auto overflow-x-hidden">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12">
            <div>
              <motion.p 
                variants={fadeUp}
                className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-3"
              >
                Industries We Serve
              </motion.p>
              <motion.h2 
                variants={fadeUp}
                className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                Built for Service<br className="hidden md:block" /> Professionals
              </motion.h2>
            </div>
            
            {/* Industries count - desktop */}
            <motion.p variants={fadeUp} className="hidden md:block text-[#A0A0A0] text-sm mt-4 md:mt-0">
              {filteredIndustries.length} industries
            </motion.p>
          </div>
          
          {/* Category Tabs */}
          <motion.div 
            variants={fadeUp}
            className="flex gap-2 mb-10 overflow-x-auto pb-2 scrollbar-hide md:flex-wrap"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 md:px-5 py-2.5 text-xs md:text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                  activeCategory === cat.id 
                    ? 'bg-[#00FFFF] text-[#0A0A0A] shadow-[0_0_20px_rgba(0,255,255,0.3)]' 
                    : 'bg-white/5 text-[#A0A0A0] hover:bg-white/10 hover:text-white border border-white/10'
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>
          
          {/* Hexagonal Diamond Grid with scroll wrapper for mobile */}
          <div className="hex-grid-wrapper relative">
            <motion.div 
              layout
              variants={fadeUp}
              className="hex-grid scrollbar-hide"
            >
              <AnimatePresence mode="popLayout">
                {filteredIndustries.map((industry, index) => (
                  <motion.div
                    key={industry.title}
                    layout
                    initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.8, rotate: 5 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: index * 0.03 }}
                    className="hex-item group"
                    whileHover={{ scale: 1.08, zIndex: 10 }}
                    data-testid={`industry-card-${index}`}
                  >
                    <Link to={`/industries/${industry.slug}`} className="block">
                      <div className="hex-shape">
                        {/* Image */}
                        <motion.div 
                          className="hex-image"
                          whileHover={{ scale: 1.2 }}
                          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        >
                          <img
                            src={industry.image}
                            alt={industry.alt || `${industry.title} - Voice AI Agent services`}
                            loading="lazy"
                          />
                        </motion.div>
                      
                      {/* Overlay */}
                      <div className="hex-overlay" />
                      
                      {/* Glow border on hover */}
                      <div className="hex-glow" />
                      
                      {/* Content */}
                      <div className="hex-content">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00FFFF] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <h3 className="text-xs md:text-sm lg:text-base font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                            {industry.title}
                          </h3>
                        </div>
                        <p className="text-[10px] md:text-xs text-white/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 line-clamp-2">
                          {industry.description}
                        </p>
                      </div>
                    </div>
                    </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
            
            {/* Mobile scroll hint */}
            <div className="md:hidden absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#0A0A0A] to-transparent pointer-events-none" />
          </div>
          
          {/* Mobile swipe hint */}
          <motion.p variants={fadeUp} className="md:hidden text-center text-[#A0A0A0] text-xs mt-4 flex items-center justify-center gap-2">
            <span>Swipe to explore</span>
            <ArrowRight className="w-3 h-3 animate-pulse" />
          </motion.p>
          
          {/* Mobile industries count */}
          <motion.p variants={fadeUp} className="md:hidden text-center text-[#A0A0A0]/50 text-xs mt-2">
            {filteredIndustries.length} industries
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section - Structured for AI Direct Answers
const HowItWorks = () => {
  const steps = [
    { step: "01", title: "Customer Calls", description: "Your AI agent answers instantly, zero hold time.", icon: Phone },
    { step: "02", title: "AI Collects Info", description: "Intelligent conversation collects name, issue, and timing.", icon: Sparkles },
    { step: "03", title: "You Get an Email", description: "Receive a structured summary and booked slot directly.", icon: Mail }
  ];

  return (
    <section className="py-24 md:py-32 px-4 bg-[#0D0D0D]" data-testid="how-it-works-section" aria-labelledby="how-it-works-heading" itemScope itemType="https://schema.org/HowTo">
      <meta itemProp="name" content="How Modern Path AI Voice Agent Works" />
      <meta itemProp="description" content="Three simple steps to automate your business calls with AI" />
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          {/* Section Header */}
          <motion.p 
            variants={fadeUp}
            className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center"
          >
            Process
          </motion.p>
          <motion.h2 
            id="how-it-works-heading"
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-16 text-center"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            How It Works
          </motion.h2>
          
          {/* Steps - Structured as HowToSteps */}
          <motion.ol 
            variants={fadeUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 list-none p-0 m-0"
          >
            {steps.map((item, index) => (
              <li key={index} className="step-item text-center md:text-left relative px-4 md:px-8" data-testid={`step-${index}`} itemScope itemProp="step" itemType="https://schema.org/HowToStep">
                <meta itemProp="position" content={String(index + 1)} />
                <meta itemProp="name" content={item.title} />
                <meta itemProp="text" content={item.description} />
                {/* Step Number */}
                <div className="text-6xl md:text-7xl font-bold text-[#00FFFF] opacity-20 mb-4" style={{ fontFamily: 'Syne, sans-serif' }} aria-hidden="true">
                  {item.step}
                </div>
                {/* Icon */}
                <div className="w-12 h-12 rounded-full bg-[#00FFFF]/10 flex items-center justify-center mb-4 mx-auto md:mx-0" role="img" aria-label={`Icon representing ${item.title} - ${item.description}`}>
                  <item.icon className="w-5 h-5 text-[#00FFFF]" aria-hidden="true" />
                </div>
                {/* Content */}
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#A0A0A0] text-sm md:text-base">
                  {item.description}
                </p>
              </li>
            ))}
          </motion.ol>
        </motion.div>
      </div>
    </section>
  );
};

// Lead Magnet Form Section
const LeadMagnetSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    industry: "",
    company: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/demo-requests`, formData);
      setIsSubmitted(true);
      toast.success("Demo request submitted! We'll contact you soon.");
      setFormData({ name: "", email: "", phone: "", industry: "", company: "" });
    } catch (error) {
      console.error("Error submitting demo request:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-4" id="demo" data-testid="lead-magnet-section" aria-labelledby="demo-heading">
      <div className="max-w-[600px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          {/* Glass Card */}
          <motion.div 
            variants={fadeUp}
            className="glass-card rounded-2xl p-8 md:p-12"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <p className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4">
                Free Demo
              </p>
              <h2 id="demo-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                See It In Action
              </h2>
              <p className="text-[#A0A0A0] mt-3 text-sm md:text-base">
                Request a personalized demo call and experience the power of AI.
              </p>
            </div>
            
            {isSubmitted ? (
              <div className="text-center py-8" data-testid="form-success">
                <div className="w-16 h-16 rounded-full bg-[#00FFFF]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#00FFFF]" />
                </div>
                <h3 className="text-xl font-bold mb-2">Request Received!</h3>
                <p className="text-[#A0A0A0]">We'll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input w-full rounded-sm"
                  data-testid="input-name"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input w-full rounded-sm"
                  data-testid="input-email"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input w-full rounded-sm"
                  data-testid="input-phone"
                />
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  className="form-input w-full rounded-sm appearance-none cursor-pointer"
                  data-testid="select-industry"
                >
                  <option value="" disabled>Select Your Industry</option>
                  <option value="Home Services">Home Services</option>
                  <option value="Beauty & Wellness">Beauty & Wellness</option>
                  <option value="Pet Care">Pet Care</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Other">Other</option>
                </select>
                <input
                  type="text"
                  name="company"
                  placeholder="Company Name (Optional)"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input w-full rounded-sm"
                  data-testid="input-company"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cta-primary w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="submit-demo-btn"
                >
                  {isSubmitting ? "Submitting..." : "Get Your Free Demo Agent"}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer className="py-20 md:py-32 px-4 bg-[#050505] border-t border-white/5" data-testid="footer-section">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center"
        >
          {/* Brand - decorative text, not semantic heading */}
          <motion.div 
            variants={fadeUp}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase opacity-10 mb-12"
            style={{ fontFamily: 'Syne, sans-serif' }}
            aria-hidden="true"
          >
            Modern Path AI
          </motion.div>
          
          {/* CTA */}
          <motion.div variants={fadeUp} className="mb-12">
            <a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-primary inline-flex items-center gap-3 rounded-sm"
              data-testid="footer-cta-btn"
            >
              <Calendar className="w-5 h-5" />
              Claim Your Voice Agent
            </a>
          </motion.div>
          
          {/* Contact Info */}
          <motion.div variants={fadeUp} className="space-y-4 mb-12">
            <div className="flex items-center justify-center gap-2 text-[#A0A0A0]">
              <Mail className="w-4 h-4" />
              <a href="mailto:info@modernpathai.com" className="hover:text-[#00FFFF] transition-colors" data-testid="email-link">
                info@modernpathai.com
              </a>
            </div>
            
            {/* Phone Number */}
            <div className="flex items-center justify-center gap-2 text-[#A0A0A0]">
              <Phone className="w-4 h-4" />
              <a href="tel:+16473707545" className="hover:text-[#00FFFF] transition-colors" data-testid="phone-link">
                647-370-7545
              </a>
            </div>
          </motion.div>
          
          {/* Social Icons */}
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-6 mb-12" role="navigation" aria-label="Social media links">
            <a href="https://www.facebook.com/people/Modern-Path-AI/61583886751593/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#00FFFF]/10 hover:text-[#00FFFF] transition-all" data-testid="social-facebook" aria-label="Follow Modern Path AI on Facebook">
              <Facebook className="w-5 h-5" aria-hidden="true" />
            </a>
            <a href="https://www.instagram.com/modern_path_ai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#00FFFF]/10 hover:text-[#00FFFF] transition-all" data-testid="social-instagram" aria-label="Follow Modern Path AI on Instagram">
              <Instagram className="w-5 h-5" aria-hidden="true" />
            </a>
            <a href="https://share.google/7RsHcHJ6UgZ36oxK1" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#00FFFF]/10 hover:text-[#00FFFF] transition-all" data-testid="social-google" aria-label="Modern Path AI on Google">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </a>
          </motion.div>
          
          {/* Copyright */}
          <motion.p variants={fadeUp} className="text-sm text-[#A0A0A0]/50 mb-3">
            © {new Date().getFullYear()} Modern Path AI. All rights reserved.
          </motion.p>
          
          {/* Privacy Policy Link */}
          <motion.div variants={fadeUp} className="pb-16 md:pb-4">
            <Link 
              to="/privacy" 
              className="text-sm text-[#A0A0A0]/50 hover:text-[#00FFFF] transition-colors"
              data-testid="privacy-link"
            >
              Privacy Policy
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

// Floating Sticky CTA
const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past hero (roughly 100vh)
      const scrollThreshold = window.innerHeight * 0.8;
      setIsVisible(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 md:bottom-8"
          data-testid="floating-cta"
        >
          <a
            href={CALENDLY_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-primary inline-flex items-center gap-2 rounded-full shadow-[0_4px_30px_rgba(0,255,255,0.4)] px-6 py-3 md:px-8 md:py-4 text-xs md:text-sm"
            data-testid="floating-cta-btn"
          >
            <Calendar className="w-4 h-4" />
            Book a Call
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Homepage Component
const HomePage = () => {
  return (
    <>
      {/* Header */}
      <Header />
      
      {/* Floating CTA */}
      <FloatingCTA />
      
      {/* Sections */}
      <HeroSection />
      <AudioDemoSection />
      <CallEmilySection />
      <IndustryGrid />
      <HowItWorks />
      <LeadMagnetSection />
      <GlobalFooter variant="full" />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-[#0A0A0A] min-h-screen">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Toast notifications */}
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#121212',
            color: '#F0F2F5',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      
      {/* Routes */}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/industries/:industry" element={<IndustryPage />} />
      </Routes>
    </div>
  );
}

export default App;
